var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import '../styles/enterKeyModal.scss';
import ButtonComponent from './ButtonComponent';
import CustomScrollbar from './CustomScrollbar';
import Popup from './Popup';
import { t } from 'i18next';
import InputBox from './InputBox';
import { getImageSrc } from '../helper/imageBlobCache';
import { API_BASE_URL } from '../config/api';
import { formatShareCode, normalizeShareCode } from '../helper/shareCode';
import { DataContext } from '../player/DataContext';
// The input field visually uses a spaced separator; backend URLs use the
// tight single-hyphen form. Both come from the shared helper.
var formatShareCodeForInput = function (raw) { return formatShareCode(raw, ' - '); };
var EnterKeyModal = function (props) {
    var setShowModal = props.setShowModal, showModal = props.showModal;
    var _a = useContext(DataContext), enterKeyPrefill = _a.enterKeyPrefill, setEnterKeyPrefill = _a.setEnterKeyPrefill, enterKeyPrefillLabel = _a.enterKeyPrefillLabel, setEnterKeyPrefillLabel = _a.setEnterKeyPrefillLabel;
    /**
     * Two modes share this component.
     *
     * Free entry (header key button): type a code, validate, open.
     *
     * Confirm (a saved row in the Save panel): the code is already known, so
     * asking the user to retype it is pointless. It names what is about to open
     * and warns that unsaved canvas work is lost — opening navigates the tab, so
     * this is the last point at which that is recoverable.
     */
    var isConfirmMode = !!enterKeyPrefill;
    // Capture the trigger button on mount (before FocusLock moves focus)
    var triggerRef = useRef(document.activeElement);
    var closeModal = function () {
        setShowModal(false);
        setEnterKeyPrefill('');
        setEnterKeyPrefillLabel('');
    };
    // Focus the input when modal opens. Confirm mode has no input — FocusLock
    // lands on the first button (Cancel) there, which is the right default for a
    // destructive-ish confirmation.
    useEffect(function () {
        if (isConfirmMode)
            return;
        var timer = setTimeout(function () {
            var input = document.getElementById('inputBox_input');
            input === null || input === void 0 ? void 0 : input.focus();
        }, 100);
        return function () { return clearTimeout(timer); };
    }, [isConfirmMode]);
    // Restore focus to trigger button on unmount
    useEffect(function () {
        return function () {
            var el = triggerRef.current;
            if (el) {
                setTimeout(function () { return el.focus(); }, 0);
            }
        };
    }, []);
    // Close on Escape across all browsers/devices
    useEffect(function () {
        var handleEsc = function (e) {
            if (e.key === 'Escape')
                closeModal();
        };
        document.addEventListener('keydown', handleEsc);
        return function () { return document.removeEventListener('keydown', handleEsc); };
    }, []);
    var _b = useState(function () { return formatShareCodeForInput(enterKeyPrefill); }), inputVal = _b[0], setInputVal = _b[1];
    var _c = useState(false), isOpening = _c[0], setIsOpening = _c[1];
    var _d = useState(false), showError = _d[0], setShowError = _d[1];
    // When the modal is reopened with a fresh prefill (e.g. user clicks another
    // code box), reseed the input instead of keeping the previous value.
    useEffect(function () {
        if (showModal)
            setInputVal(formatShareCodeForInput(enterKeyPrefill));
    }, [showModal, enterKeyPrefill]);
    var handleOpen = function () { return __awaiter(void 0, void 0, void 0, function () {
        var rawCode, hyphenatedCode, res, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (isOpening)
                        return [2 /*return*/];
                    rawCode = normalizeShareCode(inputVal);
                    if (!rawCode)
                        return [2 /*return*/];
                    setIsOpening(true);
                    hyphenatedCode = formatShareCode(rawCode);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetch("".concat(API_BASE_URL, "share-codes/").concat(encodeURIComponent(hyphenatedCode)), {
                            credentials: 'include',
                        })];
                case 2:
                    res = _b.sent();
                    if (res.ok) {
                        // Always navigate to root with the share code so any active IDM path is
                        // cleared. PlayerMainContainer.onInit will redirect to the correct
                        // activity URL (including IDM paths) once it resolves the share code's
                        // meta.url on the fresh page load.
                        window.location.href = "".concat(window.location.origin, "/?").concat(hyphenatedCode);
                        return [2 /*return*/];
                    }
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 4:
                    setShowError(true);
                    return [2 /*return*/];
            }
        });
    }); };
    return (_jsx(Popup, { isOpen: showModal, onClose: closeModal, ariaLabel: t('text.enterShareCode'), children: _jsxs("div", { className: "modalContainer", role: "form", "aria-label": t('text.enterShareCode'), children: [_jsx(CustomScrollbar, { direction: "vertical", className: "enterKeyScrollArea", children: _jsxs("div", { className: "scrollContent", children: [_jsx("h2", { className: "title", id: "enterKeyModalTitle", children: isConfirmMode ? (
                                // "Open [Name of Saved Activity] [Code]" — the name is omitted
                                // for an unlabeled save rather than rendering a double space.
                                // The code is wrapped in its own nowrap span so it always
                                // wraps as one block instead of splitting mid-code.
                                //
                                // `formatShareCode`, not the ` - ` spaced input formatter: the
                                // spaced form exists to make the code easier to TYPE, and
                                // nothing is being typed here. Using it made one code appear
                                // three ways across the UI — "DEMO0001" on the row chip,
                                // "DEMO - 0001" here, "DEMO-0001" in Display Code.
                                _jsxs(_Fragment, { children: [[t('text.open'), enterKeyPrefillLabel].filter(function (part) { return part && part.trim(); }).join(' '), ' ', _jsx("span", { className: "title-code", children: formatShareCode(enterKeyPrefill) })] })) : (t('text.enterShareCode')) }), _jsx("hr", { className: "popupHr", "aria-hidden": "true" }), isConfirmMode ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "enterKeyWarning", role: "note", children: t('text.unsavedChangesWarning') }), _jsxs("div", { className: "enterKeyConfirmButtons", children: [_jsx(ButtonComponent, { type: "textType1", text: t('text.cancel'), defaultCSS: "openBtn enterKeyCancelBtn", normalCSS: "normalBtn", clickHandler: closeModal, ariaLabel: "text.cancel" }), _jsx(ButtonComponent, { type: "textType1", text: t('text.ok'), defaultCSS: "openBtn", normalCSS: "normalBtn", clickHandler: handleOpen, disabled: isOpening, ariaLabel: "text.ok" })] }), showError && (_jsxs("div", { className: "errorMessage", children: [t('text.invalidShareCode'), ' ', _jsx("a", { href: "http://apps.mathlearningcenter.org/", target: "_blank", rel: "noopener noreferrer", children: t('text.invalidShareCodeLink') }), "."] }))] })) : (_jsxs(_Fragment, { children: [_jsx(InputBox, { id: 'inputBox', type: "text", defaultCSS: 'inputBox', value: inputVal, maxLength: 11, ariaLabel: t('text.enterShareCodeInputLabel'), autoFocus: true, onInput: function (id, val, e) {
                                            var value = val;
                                            // 1. Remove everything except letters & numbers
                                            value = value.replace(/[^a-zA-Z0-9]/g, '');
                                            // 2. Convert to uppercase
                                            value = value.toUpperCase();
                                            // 3. Insert " - " after 4 characters — must match the prefill
                                            //    formatter so the disabled-check (`length < 11`) lines up.
                                            //    The bare "-" form is only 9 chars wide and would keep the
                                            //    Open button disabled even at full length.
                                            value = formatShareCodeForInput(value);
                                            setInputVal(value);
                                            setShowError(false);
                                            setIsOpening(false);
                                        } }), showError && (_jsxs("div", { className: "errorMessage", children: [t('text.invalidShareCode'), ' ', _jsx("a", { href: "http://apps.mathlearningcenter.org/", target: "_blank", rel: "noopener noreferrer", children: t('text.invalidShareCodeLink') }), "."] })), _jsx(ButtonComponent, { type: "textType1", text: t('text.open'), defaultCSS: "openBtn", normalCSS: "normalBtn", clickHandler: handleOpen, disabled: inputVal.length < 11 || isOpening, ariaLabel: "text.open" })] }))] }) }), _jsx("div", { className: "footerModal", "aria-hidden": "true", children: _jsx("img", { src: getImageSrc('./assets/images/popupColorPatch.svg'), tabIndex: -1, alt: "" }) })] }) }));
};
export default EnterKeyModal;
